import { useEffect, useState, type ReactNode, type SyntheticEvent } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Link } from "@mui/material";
import { useAuth } from "@/context/AuthContext";
import { getModulesByUserAndClient, UserRoles } from "@/api/user.service";
import { getModulesByClient } from "@/api/client.service";

// Link de um produto para a Consulta de Produtos, usado em qualquer tela que mostre produto.

const MODULO_PRODUTO = "consulta-de-produto";

export function urlProduto(codProduto: string, codEmpresa?: string | null) {
  const params = new URLSearchParams({ produto: codProduto });
  if (codEmpresa) params.set("loja", codEmpresa);
  return `/painel/${MODULO_PRODUTO}?${params}`;
}

// Uma busca por usuário/cliente para a tela toda, não uma por card.
const acessoCache = new Map<string, Promise<boolean>>();

function useAcessoConsultaProduto() {
  const { user } = useAuth();
  const userId = user?.id;
  const clientId = user?.loggedClient?.id;
  const isAdmin = user?.role === UserRoles.Admin;
  const [acesso, setAcesso] = useState(false);

  useEffect(() => {
    if (!userId || !clientId) return;
    const chave = `${userId}:${clientId}`;
    let promessa = acessoCache.get(chave);
    if (!promessa) {
      // Admin não tem módulos próprios: vale o que está habilitado para o cliente.
      const request = isAdmin ? getModulesByClient(clientId) : getModulesByUserAndClient(userId, clientId);
      promessa = request
        .then((mods) => mods.some((m) => m.module_id === MODULO_PRODUTO))
        .catch(() => {
          acessoCache.delete(chave);
          return false;
        });
      acessoCache.set(chave, promessa);
    }
    let cancelado = false;
    promessa.then((v) => !cancelado && setAcesso(v));
    return () => {
      cancelado = true;
    };
  }, [userId, clientId, isAdmin]);

  return acesso;
}

// Sem acesso ao módulo, mostra só o conteúdo, sem link.
export function LinkProduto({
  codProduto,
  codEmpresa,
  children,
}: {
  codProduto: string;
  codEmpresa?: string | null;
  children?: ReactNode;
}) {
  const acesso = useAcessoConsultaProduto();
  const conteudo = children ?? codProduto;
  if (!acesso || !codProduto) return <>{conteudo}</>;

  // Os cards que usam o link são clicáveis: o clique no link não pode expandir o card.
  const pararPropagacao = (e: SyntheticEvent) => e.stopPropagation();

  return (
    <Link
      component={RouterLink}
      to={urlProduto(codProduto, codEmpresa)}
      underline="hover"
      color="primary"
      title="Abrir na Consulta de Produtos"
      onClick={pararPropagacao}
      onMouseDown={pararPropagacao}
      sx={{ fontWeight: 600 }}
    >
      {conteudo}
    </Link>
  );
}
